import React, { useMemo, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const AddAccount = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    accountName: "",
    bankName: "",
    accountNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAccountNumberChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    setFormData((prev) => ({ ...prev, accountNumber: value }));
    setErrors((prev) => ({ ...prev, accountNumber: "" }));
  };

  const isFormComplete = useMemo(() => {
    const { accountName, bankName, accountNumber } = formData;
    return Boolean(accountName && bankName && accountNumber);
  }, [formData]);

  const handleSubmit = async () => {
    if (!isFormComplete || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitMsg("");
    try {
      const token = localStorage.getItem("vendorToken");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/bank-details`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          accountName: formData.accountName,
          bankName: formData.bankName,
          accountNumber: formData.accountNumber,
        }),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSubmitMsg(result.message || "Bank details updated successfully.");
      } else {
        setSubmitMsg(result.message || "Error updating bank details.");
      }
    } catch (err) {
      setSubmitMsg("Error updating bank details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8" style={{ paddingLeft: "8px", paddingRight: "8px", paddingTop: "12px" }}>
      <div className="mx-auto w-full max-w-xl">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:text-gray-900"
            aria-label="Go back"
          >
            <IoArrowBack className="h-5 w-5" />
          </button>
        </div>
        <header className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">Add Account Detail</h1>
        </header>

        {submitMsg && (
          <div className="mb-4 text-center bg-[#FFBABA] text-[#800000] px-4 py-2 rounded-lg font-semibold animate-fadeInOut">
            {submitMsg}
          </div>
        )}
        <form className="mt-8 space-y-8">
          <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700">
              Bank Details (For Payouts)
            </h2>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Account Name
              </label>
              <input
                type="text"
                value={formData.accountName}
                onChange={handleFieldChange("accountName")}
                placeholder="Account name"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                  errors.accountName ? "border-red-500" : "border-gray-200"
                }`}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">Bank Name</label>
              <input
                type="text"
                value={formData.bankName}
                onChange={handleFieldChange("bankName")}
                placeholder="Bank name"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                  errors.bankName ? "border-red-500" : "border-gray-200"
                }`}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 text-left">
                Account Number
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.accountNumber}
                onChange={handleAccountNumberChange}
                placeholder="Account number"
                className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:border-[#19C463] focus:outline-none ${
                  errors.accountNumber ? "border-red-500" : "border-gray-200"
                }`}
              />
            </div>
          </section>

          <div className="space-y-3">
            <button
              type="button"
              disabled={!isFormComplete || isSubmitting}
              onClick={handleSubmit}
              className={`w-full rounded-lg py-3 text-sm font-semibold transition ${
                !isFormComplete || isSubmitting
                  ? "bg-[#A3E7C1] text-[#F1F3F2]"
                  : "bg-[#19C463] text-white"
              }`}
            >
              Add Account Details
            </button>
          </div>
        </form>
      </div>
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="rounded-[12px] bg-white p-6 flex flex-col items-center">
            <div className="h-10 w-10 border-4 border-[#19C463] border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAccount;
